"use client";

// WO-GF-001-PHASE1B S2 — the LANDING FUNNEL (W1: principal CTA → map popup → mountain select → mountain page).
// Document-scoped hydrator, same family as MapLayer / TransportSearchLayer / MarketplaceCartLayer: it wires the
// CAPTURED (dead-photocopy) home "SKI TRIP BOOKING CENTER" hero in the mirror body WITHOUT touching its DOM at
// rest, so the home pixel gate stays green. The captured funnel is inert in the mirror (JS stripped = R6-6);
// this makes it real:
//   • "BOOK NOW / Select Mountain" button + the hero logo/world-map  → open the mountain-selection popup
//     (legacy CenterLogo: both call HandleChooseMountain → setShowMountainList, pr-670).
//   • mode tiles (MARKETPLACE / TRANSPORT / DIRECTORY active; FLIGHTS / STAYS live-greyed "Coming Soon")
//     → select the mode AND open the same SELECT YOUR MOUNTAIN popup (RW-1/S10 — the founder re-walk found
//     the boxes "dead on click"); the mode sets the destination — S13/RW3-3, see destFor below.
//   • the popup = SELECT YOUR MOUNTAIN: the region list of live's own resort-sitemap mountains + a real PortMap
//     (@vis.gl, REUSED from B4 — no new map machinery); picking a mountain navigates to /mountain-<code>
//     (legacy: <a href={link.replace('[mountain]', code)}>). Live tiles are ENV-BLOCKED (F37) → PortMap's
//     interactive marker overlay, same posture as every other map surface.
// Mountain list + coords are passed from the server (page.tsx: captured resort-sitemap + getTransportData
// coords), so the list is complete and RDS-independent; pins render for mountains that carry coords.
// Harness hooks: data-behavior="funnel-cta", data-funnel-popup, data-funnel-mountain, data-funnel-mode.

import { useEffect, useRef, useState } from "react";
import { openMountainSelect } from "@/components/site/MountainSelectLayer";

export type FunnelMountain = { code: string; name: string; lat?: number; lng?: number };
/** null = the selector was opened by a NEUTRAL entry point (BOOK NOW / hero logo), with no mode chosen. */
type Mode = "marketplace" | "transport" | "directory" | null;

// WO-GF-001-P1B-S13 · RW3-3 — INTENT-PRESERVING MOUNTAIN SELECT (founder ruling (a) of this walk):
// "destination after a mountain select = the MODE that opened the selector". The founder's symptom was
// landing page → MARKETPLACE → pick a mountain → the transport/mountain landing, i.e. the mode was being
// discarded. S12/RW2-1 had collapsed EVERY mode to the hub in order to fix the transport case; this walk
// keeps that transport ruling and restores the other two, so the rule is now general rather than a
// special case:
//     marketplace → /mountain-<code>/deals-and-packages
//     transport   → /mountain-<code>            (S12 ruling (a) — the hub IS the transport surface)
//     directory   → /mountain-<code>/profile
//     (none)      → /mountain-<code>            (BOOK NOW / hero logo choose no mode — unchanged, W1)
// Those are exactly `hubNavHref`'s destinations, so the funnel and the HUD can never drift apart, and no
// new destination is introduced (every one is already a build-served page for every mountain).
// S26 · A3 — the rule is UNCHANGED, it just moved: the mode now travels with the open request and
// `MountainSelectModal` resolves the same destinations (its own `destFor`), so one selector serves every
// entry point without any of them losing their intent.

export default function LandingFunnelLayer({ mountains }: { mountains: FunnelMountain[] }) {
  // S26 · A3 — the landing entry points no longer render their OWN selector. They open the one
  // site-wide R1a selector (`MountainSelectLayer`), carrying the mode so RW3-3's intent-preserving
  // destination rule is unchanged. `mountains` is still accepted (page.tsx passes the served list) but
  // the selector reads its own list + logos + real coords, so nothing here fabricates a list.
  const [open, setOpen] = useState(false);
  // RW3-3 — the selector starts with NO mode: BOOK NOW / the hero logo choose none, so they keep
  // landing on the mountain hub (W1, unchanged). A mode tile sets one, and the destination follows it.
  const [mode, setMode] = useState<Mode>(null);
  const modeRef = useRef<Mode>(null);
  modeRef.current = mode;

  // Open the one selector whenever a landing entry point sets `open`, carrying the current mode.
  useEffect(() => {
    if (!open) return;
    openMountainSelect(modeRef.current);
    setOpen(false); // the layer owns the open state from here; this is a one-shot request
  }, [open]);

  // Wire the captured hero once mounted. We attach listeners to the photocopied elements; we never mutate
  // their DOM at rest (pixel gate safe). The "selected mode" outline is applied only on interaction.
  useEffect(() => {
    const cleanups: Array<() => void> = [];
    const openPopup = (e?: Event) => {
      e?.preventDefault();
      e?.stopPropagation();
      setOpen(true);
    };

    // 1) principal CTA — the "BOOK NOW / Select Mountain" btnPrimary submit button.
    const ctaBtn =
      Array.from(document.querySelectorAll<HTMLElement>('button, [role="button"]')).find((b) =>
        /BOOK\s*NOW/i.test(b.textContent || ""),
      ) || null;
    if (ctaBtn) {
      ctaBtn.setAttribute("data-behavior", "funnel-cta");
      ctaBtn.addEventListener("click", openPopup);
      cleanups.push(() => ctaBtn.removeEventListener("click", openPopup));
    }

    // 2) the hero logo / world-map container also opens the popup (legacy LogoContainer onClick).
    const logo = document.querySelector<HTMLElement>(
      ".LogoContainer, .centerLogo, .logoFixedPostion",
    );
    if (logo) {
      logo.style.cursor = "pointer";
      logo.addEventListener("click", openPopup);
      cleanups.push(() => logo.removeEventListener("click", openPopup));
    }

    // 2b) RW-11 (ratified 2026-07-22) — the hamburger "Book Travel" item. It is `href="#"` on LIVE (live's own
    //     defect; §0f-verified in S10), so there is no destination to derive — the founder overrode it to the
    //     TRANSPORT funnel. This reuses the RW-1 machinery exactly: open the SELECT YOUR MOUNTAIN popup preset
    //     to transport, so picking a mountain lands on the transport destination destFor() resolves —
    //     /mountain-<code> since RW2-1 ruling (a). No new destination, and it stays in step with the HUD.
    const bookTravel = document.querySelector<HTMLElement>(
      '[data-behavior="book-travel"]',
    );
    if (bookTravel) {
      const onBook = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        modeRef.current = "transport";
        setMode("transport");
        setOpen(true);
      };
      bookTravel.addEventListener("click", onBook);
      cleanups.push(() => bookTravel.removeEventListener("click", onBook));
    }

    // 3) mode tiles — MARKETPLACE / TRANSPORT / DIRECTORY are selectable; FLIGHTS / STAYS are "Coming Soon"
    //    (captured with aria-label="Coming Soon") → left inert/greyed exactly as live.
    const MODE_BY_LABEL: Record<string, Exclude<Mode, null>> = {
      "MARKET PLACE": "marketplace",
      MARKETPLACE: "marketplace",
      TRANSPORT: "transport",
      DIRECTORY: "directory",
    };
    const labels = Array.from(
      document.querySelectorAll<HTMLElement>("h6, .centerTabIcon span, [class*='subtitle']"),
    );
    for (const el of labels) {
      const key = (el.textContent || "").trim().toUpperCase();
      const m = MODE_BY_LABEL[key];
      if (!m) continue;
      // the clickable tile = the nearest MuiBox tile ancestor (fallback: the label's parent).
      const tile =
        el.closest<HTMLElement>(".MuiBox-root:not([aria-label='Coming Soon'])") ||
        (el.parentElement as HTMLElement | null);
      if (!tile || tile.getAttribute("aria-label") === "Coming Soon") continue;
      tile.setAttribute("data-funnel-mode", m);
      tile.style.cursor = "pointer";
      const h = (e?: Event) => {
        e?.preventDefault();
        e?.stopPropagation();
        modeRef.current = m;
        setMode(m);
        // reflect selection (post-interaction only → pixel gate untouched at rest).
        document
          .querySelectorAll<HTMLElement>("[data-funnel-mode]")
          .forEach((t) => (t.style.outline = t === tile ? "2px solid #f15a22" : ""));
        // RW-1 (S10) — the mode box must ACT on click. Founder re-walk: MARKETPLACE / TRANSPORT /
        // DIRECTORY were "dead on click" (they only set the internal mode, revealing nothing). Live:
        // selecting a mode advances the funnel (the selected tile centers + surfaces BOOK NOW →
        // Select Mountain). We open THE SAME SELECT YOUR MOUNTAIN popup BOOK NOW opens, preset to this
        // mode, so each active box navigates on pick via destFor (marketplace/directory → the mountain
        // hub, transport → /bus-schedule — the S2 live-derived destinations, unchanged). The BOOK NOW
        // CTA + popup machinery is untouched ("do not touch the funnel"); this only wires the boxes.
        setOpen(true);
      };
      tile.addEventListener("click", h);
      cleanups.push(() => tile.removeEventListener("click", h));
    }

    return () => cleanups.forEach((c) => c());
  }, []);

  return (
    <>
      <span data-funnel-layer hidden />
    </>
  );
}

